type ForceReloadDecisionInput = {
  appVersion: string | undefined;
  storedAppVersion: string | null;
  /** Did the location change since the previous effect run? */
  navigated: boolean;
  /** Has the version flag already resolved once for this page load? */
  hasReconciled: boolean;
};

/**
 * A version mismatch means a new bundle deployed. Reload immediately on initial
 * load (nothing in progress to lose), but defer a mid-session mismatch to the
 * next navigation so a deploy doesn't interrupt in-progress work.
 */
export const shouldForceReload = ({
  appVersion,
  storedAppVersion,
  navigated,
  hasReconciled,
}: ForceReloadDecisionInput): boolean => {
  if (!appVersion || storedAppVersion === appVersion) {
    return false;
  }
  const isInitialReconciliation = !hasReconciled;
  return navigated || isInitialReconciliation;
};

// The version flag only tracks the bundle on the real deploy. On local dev and
// Vercel previews it's an unrelated value, so a reload would just reload to the
// same code — skip it there.
export const RELOAD_ENABLED = !(import.meta.env.DEV || import.meta.env.PREVIEW);

let bootAppVersion: string | undefined;
let latestAppVersion: string | undefined;

/**
 * Record the app version reported by the version flag. The first non-empty
 * value seen this page load is the "boot" version — the build this tab is
 * actually running. Later values reflect newer deploys the flag client streams
 * in while the tab stays open.
 */
export const recordAppVersion = (appVersion: string | undefined): void => {
  if (!appVersion) {
    return;
  }
  if (bootAppVersion === undefined) {
    bootAppVersion = appVersion;
  }
  latestAppVersion = appVersion;
};

/** Pure: a newer build has shipped when both versions are known and differ. */
export const versionChanged = (
  boot: string | undefined,
  latest: string | undefined,
): boolean => Boolean(boot && latest && boot !== latest);

/**
 * True when a newer build deployed since this tab loaded. The error boundary
 * uses this to auto-reload a stale bundle that crashed (e.g. its bootstrap
 * query selects a field a newer schema dropped) instead of stranding the user
 * on the crash screen — <ForceReload> can't fire from inside the crashed
 * subtree it lives in.
 */
export const hasAppVersionChanged = (): boolean =>
  versionChanged(bootAppVersion, latestAppVersion);

/** Test-only: reset the module-level version tracking between cases. */
export const resetAppVersionTrackingForTest = (): void => {
  bootAppVersion = undefined;
  latestAppVersion = undefined;
};

const VERSION_RELOAD_KEY = "app-version-reload";

/**
 * Reload once when a version change is detected on the crash path. A
 * sessionStorage guard prevents an infinite loop if the fresh bundle still
 * crashes. The dev/preview gate lives at the
 * call site (ErrorBoundary), as it does for <ForceReload>. Cleared after a
 * successful render via clearVersionReloadFlag so the next deploy gets another
 * attempt.
 */
export const reloadOnceForVersionChange = (): boolean => {
  try {
    if (sessionStorage.getItem(VERSION_RELOAD_KEY)) {
      return false;
    }
    sessionStorage.setItem(VERSION_RELOAD_KEY, "1");
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

export const clearVersionReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(VERSION_RELOAD_KEY);
  } catch {
    // sessionStorage can throw in locked-down browsers; nothing to clear.
  }
};
